import type { ComponentProps } from 'react'
import { View, Platform, StyleSheet } from 'react-native'
import { SvgUri } from 'react-native-svg'
import { useMediaQuery } from 'react-responsive'
import { mesaColorNames, typography } from '@/src/common/theme'
import { Image } from 'expo-image'
import { maxWidth, useMainWrapperPadding } from '@/src/common/wrappers/MainWrapper'
import { Anchor, Text } from 'tamagui'

export const paddingBottom = 64

export function MesaFooter({ style }: Pick<ComponentProps<typeof View>, 'style'>) {
  return (
    <View style={[{ display: 'flex', flexDirection: 'column', gap: 40, paddingTop: 60 }, style]}>
      {Platform.OS === 'web' && <FooterContentComponent />}
    </View>
  )
}

function FooterAnchor(
  props: ComponentProps<typeof Anchor> & { textProps?: ComponentProps<typeof Text> },
) {
  return (
    <Anchor {...props} style={[{ textDecorationLine: 'none', lineHeight: 16 }, props.style]}>
      <Text
        animation="100ms"
        hoverStyle={{ color: mesaColorNames['Mesa Primary Scale/500 #00C7B6'] }}
        {...props.textProps}
        style={[
          typography['paragraph/sm'],
          { lineHeight: 16, color: mesaColorNames['Mesa - Brand/Blackish #222222'] },
          props.textProps?.style,
        ]}
      >
        {props.children}
      </Text>
    </Anchor>
  )
}

function useIsFooterSplit() {
  return useMediaQuery({ minWidth: 1024 })
}

function CenteredFooterAnchor(props: ComponentProps<typeof FooterAnchor>) {
  const isFooterSplit = useIsFooterSplit()
  return <FooterAnchor style={!isFooterSplit && { textAlign: 'center' }} {...props} />
}

function FooterText(props: ComponentProps<typeof Text>) {
  return (
    <Text
      color={mesaColorNames['Mesa - Brand/Blackish #222222']}
      {...props}
      style={[typography.XS, StyleSheet.flatten([props.style])]}
    />
  )
}

function LightFooterAnchor(props: ComponentProps<typeof FooterAnchor>) {
  return (
    <FooterAnchor
      {...props}
      hoverStyle={{ color: mesaColorNames['Mesa Primary Scale/500 #00C7B6'] }}
      textProps={{
        style: { color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'] },
      }}
    />
  )
}

function FooterContentComponent() {
  const isFooterSplit = useIsFooterSplit()
  const mainWrapperPadding = useMainWrapperPadding()
  const isFooterCrammed = useMediaQuery({ maxWidth: 768 })

  return (
    <>
      <View
        style={[
          {
            maxWidth,
            paddingHorizontal: mainWrapperPadding,
            marginHorizontal: 'auto',
            display: 'flex',
          },
          isFooterSplit ? { flexDirection: 'row', gap: 140 } : { flexDirection: 'column', gap: 40 },
        ]}
      >
        <View style={{ flex: 1 }}>
          <SvgUri
            width={100}
            height={38.5}
            uri="https://cdn.prod.website-files.com/66ba8f888ae5ad6954fd9036/66ba9a9d8dfa6f321aa3e71d_logo_dualtone--blue.svg"
          />
          <FooterText>
            {`
LaylaTex Bowie LLC dba Mesa Mortgage NMLS # 2685861
For licensing information visit our `}
            <FooterAnchor
              href="https://www.mesamember.com/about"
              style={{ lineHeight: 18 }}
              textProps={{ style: typography.XS }}
            >
              Disclosure and Licensing Page
            </FooterAnchor>
            {`
Mesa Mortgage is not available in all states

Equal Housing Statement Mesa Mortgage complies with Equal Housing Opportunity laws and regulations in compliance with the Fair Housing Act. As prohibited by federal law, we do not engage in business practices that discriminate on the basis of race, color, religion, national origin, sex, marital status, age (provided you have the capacity to enter into a binding contract), because all or part of your income may be derived from any public assistance program, or because you have, in good faith, exercised any right under the Consumer Credit Protection Act. The federal agency that administers our compliance with these federal laws is the Federal Trade Commission, Equal Credit Opportunity, Washington, DC, 20580.

`}
          </FooterText>
          <Image
            source="https://cdn.prod.website-files.com/66ba8f888ae5ad6954fd9036/6826e806609ecb2e14ba4507_EEO.png"
            style={{ width: 64, height: 64 }}
          />
          <FooterText>{`
Mesa is a technology company not a bank. The Mesa Homeowners Visa® Signature Preferred Credit Card is Powered by Highnote and issued by Celtic Bank, pursuant to a license from Visa U.S.A. Visa is a registered trademark of Visa Inc. All cards are subject to credit approval.

Copyright © 2025 Mesa Technologies Inc. | All Rights Reserved
`}</FooterText>
        </View>
        <View
          style={[
            { display: 'flex', flexBasis: 'auto' },
            isFooterCrammed
              ? { flexDirection: 'column', gap: 40 }
              : { flexDirection: 'row', gap: 60, justifyContent: 'space-around' },
          ]}
        >
          {(
            [
              {
                title: 'Company',
                content: (
                  <>
                    <CenteredFooterAnchor href="https://www.mesamember.com/about">
                      About
                    </CenteredFooterAnchor>
                    <CenteredFooterAnchor href="https://www.mesamember.com/careers">
                      Careers
                    </CenteredFooterAnchor>
                    <View style={{ display: 'flex', flexDirection: 'row', gap: 12 }}>
                      <Anchor href="https://www.linkedin.com/company/mesamember">
                        <SvgUri
                          width={24}
                          height={24}
                          style={{ flexBasis: 'auto' }}
                          uri="https://cdn.prod.website-files.com/62434fa732124a0fb112aab4/62434fa732124a389912aad8_linkedin%20small.svg"
                        />
                      </Anchor>
                      <Anchor href="https://www.instagram.com/mesamember">
                        <Image
                          style={{ flexBasis: 'auto', width: 20, height: 20 }}
                          source="https://cdn.prod.website-files.com/66ba8f888ae5ad6954fd9036/6734efc3ebb1bad7e0287d58_instagram-logo.png"
                        />
                      </Anchor>
                    </View>
                  </>
                ),
              },
              {
                title: 'Resources',
                content: (
                  <>
                    <CenteredFooterAnchor href="https://www.mesamember.com/about#press">
                      Press
                    </CenteredFooterAnchor>
                    <CenteredFooterAnchor href="https://www.mesamember.com/contact">
                      Contact
                    </CenteredFooterAnchor>
                  </>
                ),
              },
              {
                title: 'Legal',
                content: (
                  <>
                    <CenteredFooterAnchor href="https://www.mesamember.com/legal/mesa-mortgage-terms-of-use">
                      Terms of Use
                    </CenteredFooterAnchor>
                    <CenteredFooterAnchor href="https://www.mesamember.com/legal/privacy-policy">
                      Privacy Policy
                    </CenteredFooterAnchor>
                    <CenteredFooterAnchor href="https://www.mesamember.com/legal/mesa-mortgage-disclosures-licensing">
                      Disclosures & Licensing
                    </CenteredFooterAnchor>
                    <CenteredFooterAnchor href="https://cdn.prod.website-files.com/66ba8f888ae5ad6954fd9036/6826eb8a139d0ff6b72323cd_Mesa%20Mortgage%20GLBA%20Notice%20Draft%20April%202025.pdf">
                      GLBA Notice
                    </CenteredFooterAnchor>
                    <CenteredFooterAnchor href="https://www.mesamember.com/legal/mesa-mortgage-affiliated-business-arrangement-disclosure">
                      Affiliated Business Arrangement
                    </CenteredFooterAnchor>
                  </>
                ),
              },
            ] as const
          ).map(({ title, content }) => (
            <View
              key={title}
              style={[
                { display: 'flex', flexDirection: 'column', gap: 24 },
                isFooterSplit ? { flex: 1, alignItems: 'flex-start' } : { alignItems: 'center' },
              ]}
            >
              <FooterText style={[typography['heading/heading5'], { textTransform: 'uppercase' }]}>
                {title}
              </FooterText>
              {content}
            </View>
          ))}
        </View>
      </View>
      <View
        style={{
          width: '100%',
          backgroundColor: mesaColorNames['Mesa - Brand/Secondary #223843'],
          paddingVertical: paddingBottom,
        }}
      >
        <Text
          style={[
            {
              width: '80%',
              marginLeft: 'auto',
              marginRight: 'auto',
              color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
            },
            typography.S,
          ]}
        >
          {`* Up to 100,000 points annually. Subject to spending $1,000 in purchases on your card in the same statement period as your mortgage payment.

¹ Rewards Terms and Conditions `}
          <LightFooterAnchor
            href="https://www.mesamember.com/legal/mesa-membership-terms-and-conditions"
            hoverStyle={{ color: mesaColorNames['Mesa Primary Scale/500 #00C7B6'] }}
            textProps={{
              style: { color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'] },
            }}
          >
            here
          </LightFooterAnchor>
          {`
² Cardholder Agreement `}
          <LightFooterAnchor
            href="https://cdn.prod.website-files.com/66ba8f888ae5ad6954fd9036/6761da31f12c58c91158277b_CP-2%20CHA%20Mesa%20Homeowners%20Card.pdf"
            hoverStyle={{ color: mesaColorNames['Mesa Primary Scale/500 #00C7B6'] }}
            textProps={{ color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'] }}
          >
            here
          </LightFooterAnchor>
          {`
³ Benefit Terms and Conditions `}
          <LightFooterAnchor
            href="https://www.mesamember.com/legal/mesa-homeowners-card-benefit-terms-of-use"
            hoverStyle={{ color: mesaColorNames['Mesa Primary Scale/500 #00C7B6'] }}
            textProps={{ color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'] }}
          >
            here
          </LightFooterAnchor>
          {`

Mesa is a technology company not a bank. The Mesa Homeowners Visa® Signature Preferred Credit Card is issued by Celtic Bank, pursuant to a license from Visa U.S.A. Visa is a registered trademark of Visa Inc. Powered by Highnote. All cards are subject to credit approval.`}
        </Text>
      </View>
    </>
  )
}
